import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Flex, ModalFooter, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import StackedButtonGroup from '../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import ProcoreWritebackWarning from '../../../../../components/ProcoreWritebackWarning';
import { AdminSettingsPageDuskIds } from '../../../../../constants/duskIds';
import {
  deleteDepartment,
  resetAdminPageCurrentDepartment,
  resetMoveDepartmentData,
  selectMoveDepartmentData,
  skipMoveDepartmentData,
  transferDepartment
} from '../../../../../redux/modules/adminPage';
import { hideModal, modalGoBack } from '../../../../../redux/modules/modal';
import { departmentProcoreWritebackQuery } from '../../../../../utils/procoreWritebackUtil';
import MoveDepartmentsSelectBox from '../../../panels/DepartmentsTabPanel/components/MoveDepartmentsSelectBox';

const ConfirmDeleteModalDepartment = ({ data, hasData, departmentMoved, checkForDataTransfer }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const moveDepartmentData = useSelector(selectMoveDepartmentData);

  // Refetched on every open: transferring projects out, the step before
  // deleting, changes how many records still have Procore rows. Until it
  // answers -- or if it cannot -- the warning shows.
  const procore = useQuery(departmentProcoreWritebackQuery(data?.departmentId));
  const procoreStatus = procore.isSuccess ? procore.data : { enabled: true, affectedRecords: null };
  const procoreWarning = procoreStatus.enabled && procoreStatus.affectedRecords !== 0 && (
    <ProcoreWritebackWarning affectedRecords={procoreStatus.affectedRecords} scope="department" />
  );

  const deleteCurrentDepartment = async () => {
    setSubmitting(true);
    await dispatch(deleteDepartment());
    await dispatch(hideModal());
    dispatch(resetMoveDepartmentData());
    dispatch(resetAdminPageCurrentDepartment());
    setSubmitting(false);
  };

  const onCancel = () => {
    dispatch(hideModal());
    dispatch(resetAdminPageCurrentDepartment());
    dispatch(resetMoveDepartmentData());
  };

  const getModalBody = () => {
    let deleteDepartmentMessage;
    let modalFooter;

    if (departmentMoved || !hasData) {
      deleteDepartmentMessage = (departmentMoved || !hasData) && (
        <VStack width="100%">
          <Text color="greyScale.800" pb={4}>
            Are you sure you want to delete the following department?
          </Text>
          <Text color="greyScale.900" fontWeight="medium">
            {data.departmentName}
          </Text>
          {procoreWarning}
        </VStack>
      );

      modalFooter = (
        <StackedButtonGroup
          dusk={AdminSettingsPageDuskIds.confirmDeleteDepartmentButton}
          isLoading={isSubmitting}
          label="Delete Department"
          primaryButtonVariant="gradientError"
          secondaryClick={onCancel}
          secondaryLabel="Cancel"
          onClick={deleteCurrentDepartment}
        />
      );
    } else if (hasData) {
      if (!checkForDataTransfer && !departmentMoved) {
        deleteDepartmentMessage = (
          <VStack spacing={4}>
            <Text color="greyScale.800" fontWeight="medium">
              {`The selected department contains data. Deleting the department will all result in permanently deleting all of its data as well, including projects,
          email templates, and roles. If you wish to save the data within the department,
          click back to the previous step and transfer the data to a different department before deleting.`}
            </Text>
            {procoreWarning}
          </VStack>
        );

        modalFooter = (
          <VStack>
            <Checkbox pb={3} onChange={() => setIsChecked(!isChecked)}>
              <Text color="greyScale.900" textStyle="bodyMd500">I understand that deleting this department will also delete all the data within the department.</Text>
            </Checkbox>
            <StackedButtonGroup
              isDisabled={!isChecked}
              isLoading={isSubmitting}
              label="Delete Department"
              primaryButtonVariant="gradientError"
              secondaryClick={() => {
                dispatch(resetMoveDepartmentData());
                dispatch(modalGoBack());
              }}
              secondaryDisabled={isSubmitting}
              secondaryLabel="Back"
              onClick={deleteCurrentDepartment}
            />
          </VStack>
        );
      } else {
        deleteDepartmentMessage = (
          <>
            <VStack color="greyScale.800" pb={4}>
              <Text>The department you have selected to delete contains data.</Text>
              <Text fontWeight="medium">Transfer the data to a different department or it will be permanently deleted.</Text>
            </VStack>

            <MoveDepartmentsSelectBox department={data} label="Transfer Data To:" />
          </>
        );

        modalFooter = (
          <StackedButtonGroup
            isDisabled={!moveDepartmentData?.toDepartmentId}
            isLoading={isSubmitting}
            label="Transfer Data"
            loadingText="Transferring"
            secondaryClick={() => dispatch(skipMoveDepartmentData())}
            secondaryLabel="Skip"
            onClick={() => dispatch(transferDepartment())}
          />
        );
      }
    }

    return { deleteDepartmentMessage, modalFooter };
  };

  return (
    <Flex alignItems="center" direction="column" pt={2}>
      <Text fontSize="md" pb={4}>
        {getModalBody().deleteDepartmentMessage}
      </Text>
      <ModalFooter>
        {getModalBody().modalFooter}
      </ModalFooter>
    </Flex>
  );
};

ConfirmDeleteModalDepartment.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  checkForDataTransfer: PropTypes.bool,
  hasData: PropTypes.bool,
  departmentMoved: PropTypes.bool
};

export default ConfirmDeleteModalDepartment;
